(function () {
  'use strict';

  var SECTION_ID = (document.currentScript && document.currentScript.dataset.sectionId) || '';

  function withGSAP(cb) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
      cb();
    }
  }

  function initSection() {
    var section = document.getElementById('aps-' + SECTION_ID);
    if (!section) return;

    var stage        = section.querySelector('.aps-stage');
    var sticky       = section.querySelector('.aps-sticky');
    var handleStage  = section.querySelector('[data-aps-handle-stage]');
    var headlineStack = section.querySelector('[data-aps-headline-stack]');
    var headlineLayers = Array.from(section.querySelectorAll('[data-aps-headline-layer]'));
    var imagesLayer  = section.querySelector('.aps-images');
    var images       = Array.from(section.querySelectorAll('[data-aps-image]'));
    var testimonial  = section.querySelector('.aps-testimonial');
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Mobile: scrub the image-card parallax 1:1 with scroll (scrub:true), not
    // with a 1s smoothing lag. On slower Android the lag made the cards drift
    // "behind" the scroll and never quite settle.
    var apsIsMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    var apsImgScrub = reduceMotion ? false : (apsIsMobile ? true : 1);

    var triggers = [];
    // Anything that isn't a ScrollTrigger (window-level listeners, mainly)
    // but still needs tearing down on section unload — Shopify's theme
    // editor re-runs initSection() on every settings change, and a
    // window-scoped listener added without a matching removal would
    // accumulate one more copy on every single edit, which is a very
    // plausible cause of an editor that gradually grinds to a halt.
    var cleanupFns = [];

    /* Image containers stay fully hidden (no fade — an instant show/hide)
       until the headline is actually pinned, otherwise ones positioned
       near the top of the stage would already be scrolling past in normal
       flow before the pin engages. Once revealed, their own entrance
       (scale/rotate) and drift already carry them upward through the
       viewport — no extra fade is layered on top of that. */
    if (imagesLayer && !reduceMotion) {
      imagesLayer.style.visibility = 'hidden';
    }

    /* ── Section reveal ───────────────────────────────────────────────
       Top-only entrance reveal. The shape IS what reveals — no opacity,
       no scaleX/xPercent centering hack (width is always 100vw, exactly
       the container's own width, so it never needs centering in the
       first place). It starts as a very flat, fully-rounded dome
       (border-radius 100% on its top corners) and, via scaleX/scaleY +
       that radius animating down to 0%, stretches while flattening into
       a plain rectangle — the persistent linear-gradient painted ONTO
       the shape itself (white at both its own 0%/100%) is what
       naturally produces a white margin at the true top of the section
       once it's grown to cover it.
       scaleX grows too (not just scaleY) — at rest (small) it was
       staying full-width (scaleX effectively 1) while very short, which
       showed a faint blurred vertical margin at the left/right screen
       edges the whole time (blurring a hard edge still shows SOME
       falloff even when that edge sits exactly at the viewport
       boundary); growing scaleX slightly past 1 once fully revealed
       pushes that same blurred margin off-screen instead, where it's
       harmless.
       `top` offset is 25% (not 0) with a matching upward yPercent
       riding in as it grows — TUNE LIVE, first pass, not measured
       against the real rendered geometry.
       Runs entranceProgress (0→1) directly and freezes fully-grown once
       that reaches 1. */
    var revealShape = section.querySelector('[data-aps-reveal]');
    var revealEntranceProgress = 0;
    if (revealShape) gsap.set(revealShape, { transformOrigin: '50% 0%' });

    function applyReveal(progress) {
      if (!revealShape) return;
      var p = Math.min(Math.max(progress, 0), 1);
      revealShape.style.visibility = p > 0 ? 'visible' : 'hidden';
      var radius = gsap.utils.interpolate(100, 0, p) + '%';
      // Upward pull of the shape as it grows. On desktop it tucks the
      // white 0% stop off the top edge. On mobile the shape already sits
      // flush (top:0 in the SCSS) and the blur is soft — any upward pull
      // just bled the blurred dome up over the previous section, so pull
      // is 0 there (the white 0% stop then reads as the section's soft top
      // margin, which is what it's meant to be anyway).
      var isMobile = window.matchMedia('(max-width: 767px)').matches;
      var yPull = isMobile ? 0 : 25;
      gsap.set(revealShape, {
        scaleX: gsap.utils.interpolate(0.5, 1.08, p),
        // End scale kept deliberately low (was 2.2): .aps-reveal is now
        // sized in vh (see the SCSS), and 2.2× of that would again leave
        // the grown, blurred shape hanging viewports below the stage and
        // bleeding its still-coloured lower half into the following
        // section. 1.45 covers the pinned viewport with just enough of a
        // tail for the gradient's own white 100% stop to finish softly
        // inside the testimonial. TUNE LIVE — single knob.
        scaleY: gsap.utils.interpolate(0.15, 1.45, p),
        yPercent: -gsap.utils.interpolate(0, yPull, p),
        borderTopLeftRadius: radius,
        borderTopRightRadius: radius
      });
    }

    applyReveal(revealEntranceProgress);

    // Same viewport-height entrance window used before: 'top bottom' →
    // 'top top', i.e. exactly the one screenful of scroll it takes for
    // the section to fully arrive.
    triggers.push(ScrollTrigger.create({
      trigger: section,
      start: 'top bottom',
      end: 'top top',
      scrub: true,
      onUpdate: function (self) {
        revealEntranceProgress = self.progress;
        applyReveal(revealEntranceProgress);
      }
    }));

    /* ── Gradient color reveal, tied to .aps-sticky-spacer: as the user
       scrolls THROUGH that 100dvh gap (between the handle sticking and
       the headline arriving), --aps-gradient-pos-1 animates from its
       authored default down to 0% — i.e. by the time the spacer has
       fully scrolled past, the white lead-in has been squeezed out of
       the gradient entirely and stage 1's color already occupies the
       whole top of it. Reads the CURRENT computed value as the start
       point (not a hardcoded duplicate of the Liquid default) so it
       still works correctly if that default is ever changed. */
    var stickySpacer = section.querySelector('.aps-sticky-spacer');
    if (stickySpacer) {
      var gradientPos1Start = parseFloat(getComputedStyle(section).getPropertyValue('--aps-gradient-pos-1')) || 32.69;
      triggers.push(ScrollTrigger.create({
        trigger: stickySpacer,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        onUpdate: function (self) {
          var pos1 = gsap.utils.interpolate(gradientPos1Start, 0, self.progress);
          section.style.setProperty('--aps-gradient-pos-1', pos1 + '%');
        }
      }));
    }

    /* ── Gradient color crossfade, 4 phases (editable in the schema):
       1 Start, 2/3 the two "Schriftwechsel" stage switches, 4 the
       testimonial's entrance. colorProgress is a single 0→3 scalar —
       whole numbers land exactly on an authored phase, fractions
       crossfade linearly between the two phases either side of it, so
       nothing ever snaps. Phases 1–3 come from pinTrigger's own 0→1
       progress (×2, since 3 phases = 2 gaps between them); phase 3→4
       comes from a separate testimonial-entrance trigger below, added
       on top once the stage ones cap out at 2. gsap.utils.interpolate
       handles the hex color math natively. */
    var GRADIENT_PHASES = JSON.parse(section.dataset.gradientPhases || '[]');
    var stageColorProgress = 0;
    var testimonialColorProgress = 0;

    function applyGradientColors() {
      if (GRADIENT_PHASES.length < 4) return;
      var total = Math.min(2, stageColorProgress) + testimonialColorProgress;
      var idx = Math.min(2, Math.floor(total));
      var t = total - idx;
      var from = GRADIENT_PHASES[idx];
      var to = GRADIENT_PHASES[idx + 1];
      section.style.setProperty('--aps-gradient-color-1', gsap.utils.interpolate(from.c1, to.c1, t));
      section.style.setProperty('--aps-gradient-color-2', gsap.utils.interpolate(from.c2, to.c2, t));
      section.style.setProperty('--aps-gradient-edge', gsap.utils.interpolate(from.edge, to.edge, t));
    }

    var stickyInner = section.querySelector('.aps-sticky__inner');

    /* ── Headline: font swap across up to 3 stacked layers (one per font/
       weight/size stage, all sharing the same grid cell). Three selectable
       transition styles — "pop" and "stack" are the exact same techniques
       as the Upgrade Hero's reveal styles (word pop-in/slide-in wave /
       split-flap block flip), just replayed forward AND backward on every
       scroll-triggered stage change instead of once on page load.
       Whichever style runs, the .aps-headline-stack container's own height
       is measured and animated to match the incoming layer on every swap,
       so the gap to the copy text below always tracks what's actually on
       screen instead of staying pinned to the tallest possible stage. ── */
    var applyStage = function () {};
    if (headlineLayers.length) {
      var currentStage = 0;
      var transitionMode = headlineStack ? (headlineStack.getAttribute('data-headline-transition') || 'crossfade') : 'crossfade';
      var canSplit = transitionMode !== 'crossfade' && typeof SplitText !== 'undefined' && !reduceMotion;
      if (transitionMode !== 'crossfade' && !canSplit) transitionMode = 'crossfade';

      // offsetHeight (layout box) rather than getBoundingClientRect() —
      // the latter reflects the crossfade's transform:scale mid-transition
      // and would feed a distorted reading back into the height target.
      function layerHeight(layer) {
        return layer.offsetHeight;
      }

      function syncStackHeight(layer, animate, onComplete) {
        if (!headlineStack || !layer) { if (onComplete) onComplete(); return; }
        var target = layerHeight(layer);
        if (reduceMotion || !animate) {
          gsap.set(headlineStack, { height: target });
          if (onComplete) onComplete();
        } else {
          gsap.to(headlineStack, { height: target, duration: 0.3, ease: 'power2.inOut', onComplete: onComplete });
        }
      }

      if (headlineStack) {
        gsap.set(headlineStack, { height: headlineStack.getBoundingClientRect().height });
        syncStackHeight(headlineLayers[currentStage], false);
        var _stackResizeW = window.innerWidth;
        var onStackResize = function () {
          if (window.innerWidth === _stackResizeW) return; // ignore iOS address-bar height-only resizes
          _stackResizeW = window.innerWidth;
          syncStackHeight(headlineLayers[currentStage], false);
        };
        window.addEventListener('resize', onStackResize);
        cleanupFns.push(function () { window.removeEventListener('resize', onStackResize); });
      }

      if (transitionMode === 'crossfade') {
        applyStage = function (i) {
          if (i === currentStage) return;
          currentStage = i;
          headlineLayers.forEach(function (layer, idx) {
            layer.classList.toggle('is-active', idx === i);
          });
          syncStackHeight(headlineLayers[i], true);
        };
      } else {
        // Pre-split every layer once. type:'chars' alone was silently
        // producing zero split characters in the bundled GSAP build (see
        // Upgrade Hero's initPopReveal) — 'words, chars' together is the
        // combo that actually works. "stack" uses the chars (clips the
        // whole headline-stack container and moves entire layers together
        // through it, mirroring the Hero's split-flap reveal); "pop" uses
        // the words (mirrors the Hero's word pop-in/slide-in wave).
        var layerSplits = headlineLayers.map(function (layer) {
          return SplitText.create(layer, {
            type: 'words, chars',
            wordsClass: 'aps-headline-word',
            charsClass: 'aps-headline-char'
          });
        });

        // Pre-draw the pill-mark border on every NON-active layer up front
        // (force .is-revealed so the 0.9s conic draw-in runs now, while the
        // ring is still invisible) and hold each such ring at opacity:0.
        // popPillMarks() during a stage swap then only ever fades/scales an
        // ALREADY-DRAWN border in. Without this the first forward swap ran
        // popPillMarks() against a ring whose border was still mask-hidden
        // (--mark-progress:0%) because the generic IntersectionObserver in
        // academy-pill-mark.js hadn't revealed that layer yet — so nothing
        // popped in on the first font change, and it only looked right once
        // you scrolled back and the border had since been drawn. The
        // initially-active layer (currentStage) is deliberately left to the
        // generic observer so its border still draws on scroll-in as before.
        function primeInactivePillMarks() {
          headlineLayers.forEach(function (layer, idx) {
            if (idx === currentStage) return;
            layer.querySelectorAll('.academy-pill-mark').forEach(function (m) {
              m.classList.add('is-revealed');
            });
            var rings = layer.querySelectorAll('.academy-pill-mark__ring');
            if (rings.length) gsap.set(rings, { opacity: 0 });
          });
        }
        primeInactivePillMarks();

        var activeTween = null;

        // Custom @font-face fonts here all use font-display: swap, so the
        // SplitText call above can run against fallback-font metrics if a
        // stage change is triggered (or this section is measured) before
        // the real font has finished downloading. That leaves every char's
        // travel distance and the stack's clip-window height sized for the
        // WRONG font — once the real font swaps in, the reveal visibly
        // trails behind it instead of cleanly masking it, which is exactly
        // the "wipes behind the new font-family" bug. Re-split and re-sync
        // once fonts are actually ready, but only when nothing is mid-
        // flight (activeTween) — never yank the DOM out from under a
        // running swap.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () {
            if (activeTween) return;
            layerSplits.forEach(function (split) { split.revert(); });
            layerSplits = headlineLayers.map(function (layer) {
              return SplitText.create(layer, {
                type: 'words, chars',
                wordsClass: 'aps-headline-word',
                charsClass: 'aps-headline-char'
              });
            });
            // split.revert() above restores each layer's pre-split HTML,
            // which drops the .is-revealed class primed below — re-apply it.
            primeInactivePillMarks();
            syncStackHeight(headlineLayers[currentStage], false);
          });
        }

        // Fully hides a layer: removes .is-active AND clears the inline
        // opacity that gsap.set(inLayer, {opacity:1}) leaves behind — the
        // class alone isn't enough, since an inline style always wins over
        // the CSS class's opacity:0 rule. Without this, every layer that
        // was ever "incoming" stayed permanently visible, and after
        // cycling through all 3 stages you'd end up with all three —
        // including their pill-mark borders — stacked on screen at once.
        function hideLayer(layer) {
          if (!layer) return;
          layer.classList.remove('is-active');
          gsap.set(layer, { opacity: 0, zIndex: 0 });
        }

        // Unlike crossfade (a blurred opacity dissolve, where any mismatch
        // between the outgoing/incoming pill-mark borders is masked by the
        // blur), wipe/stack/pop keep BOTH layers at full opacity for the
        // whole transition — so both their already-drawn pill-mark borders
        // would render at once, and since the marked word is a different
        // size per font stage, they wouldn't line up: it'd read as two
        // pills. Instead of trying to keep the pill synced to the sliding/
        // popping text underneath it (which is what caused the earlier
        // "pill doesn't keep up" bug — a scale/position mismatch is far
        // more noticeable than a clean disappear+reappear), the outgoing
        // pill just vanishes instantly and the incoming one pops in via
        // an elastic scale.
        //
        // Only the RING (the .academy-pill-mark__ring overlay — see the
        // shared snippet/SCSS) gets this scale treatment, never
        // .academy-pill-mark itself: that span also wraps the marked
        // word's actual text, which is already mid-flight through its own
        // SplitText char/word tween at this point. Scaling the whole
        // span used to layer a second, unrelated motion on top of that
        // word only, so it visibly moved differently from every other
        // word in the headline — the ring is a separate, absolutely
        // positioned child, so popping it never touches the text at all.
        function popPillMarks(layer, show) {
          if (!layer) return;
          var rings = layer.querySelectorAll('.academy-pill-mark__ring');
          if (!rings.length) return;
          gsap.killTweensOf(rings);
          if (show) {
            // Calm grow-in, not a bouncy pop: elastic.out's overshoot read
            // as too busy landing right on top of the text switch itself.
            // Still starts from a slightly-shrunk, faded state (not fully
            // scale:0/opacity:0) so it reads as a gentle settle-in rather
            // than a hard appear.
            gsap.set(rings, { scale: 0.85, opacity: 0 });
            // No extra delay here on purpose: by the time this runs, the
            // caller has already waited for the switch effect (and the
            // headline-stack height sync that moves the copy text below
            // into its final spot) to fully finish — see applyStage's
            // onComplete chains below. The pill is always the LAST thing
            // to appear in a stage change, never mid-flight.
            gsap.to(rings, { scale: 1, opacity: 1, duration: 0.45, ease: 'power2.out' });
          } else {
            // A quick real fade (not an instant cut) — short enough
            // (0.12s) that it's well clear before the incoming ring
            // could ever start its own grow-in, so the two still never
            // overlap, but a hard instant snap read as an abrupt flash
            // rather than a deliberate disappear.
            gsap.to(rings, { scale: 0, opacity: 0, duration: 0.12, ease: 'power1.in' });
          }
        }

        applyStage = function (i) {
          if (i === currentStage) return;
          var outIndex = currentStage;
          currentStage = i;

          var outLayer = headlineLayers[outIndex];
          var inLayer = headlineLayers[i];

          if (activeTween) activeTween.kill();

          // Defensive cleanup: if a previous swap got interrupted mid-
          // animation (fast scrolling through multiple stages before its
          // tween finished), activeTween.kill() above stops it but never
          // runs its onComplete — so any layer other than the two involved
          // here might still be stuck visible from an earlier swap. Force
          // them hidden every time, not just on the mode's own onComplete.
          headlineLayers.forEach(function (layer, idx) {
            if (idx !== outIndex && idx !== i) hideLayer(layer);
          });

          // The incoming pill is deliberately NOT triggered here: it must
          // only appear once the switch effect has fully finished AND the
          // headline-stack height has resettled (which is what shifts the
          // copy text below into its new final position) — never while
          // either is still mid-flight. Each branch below wires it into
          // its own onComplete chain instead.
          popPillMarks(outLayer, false);

          // Both layers sit at opacity:1 for the whole slide (see the
          // comment above popPillMarks), and neither has any other
          // stacking context — so without an explicit z-index, paint
          // order just follows DOM order: whichever layer is LATER in
          // the markup renders on top regardless of which one is
          // actually entering. Forward transitions (stage 1→2→3) always
          // happened to have the incoming layer later in the DOM, so
          // this went unnoticed — but scrolling BACKWARD (e.g. 3→2) has
          // the OUTGOING layer later in the DOM, so it painted on top of
          // the incoming one all through the slide: the incoming text
          // (and its pill-mark border) stayed hidden underneath the
          // still-visible outgoing text instead of the outgoing text
          // properly sliding away, which is exactly the "text wipes
          // behind the new one instead of out of the viewport" / "pill
          // doesn't keep up" bug. Explicit z-index fixes this for both
          // directions.
          gsap.set(outLayer, { zIndex: 1 });
          gsap.set(inLayer, { opacity: 1, zIndex: 2 });
          inLayer.classList.add('is-active');

          if (transitionMode === 'pop') {
            // Height sync runs concurrently (it's the shorter of the two —
            // 0.3s vs. the pop's own ~0.55s total below), so the copy text
            // is already settled into its final gap well before the pop
            // finishes and the pill is allowed to show.
            syncStackHeight(inLayer, true);

            var outWords = layerSplits[outIndex].words;
            var inWords = layerSplits[i].words;

            gsap.set(inWords, { scale: 0, xPercent: 100 });
            activeTween = gsap.timeline({
              onComplete: function () {
                hideLayer(outLayer);
                popPillMarks(inLayer, true);
              }
            })
              .to(outWords, { scale: 0, xPercent: -60, duration: 0.18, stagger: 0.012, ease: 'power2.in' }, 0)
              // Outgoing layer itself (not just its words) fades out over
              // the SAME window the words shrink through — belt-and-
              // suspenders so the old font is guaranteed gone via opacity
              // even if the scale-to-0 doesn't visually read as "gone" for
              // every word shape/size, instead of only disappearing later
              // in hideLayer()'s onComplete.
              .to(outLayer, { opacity: 0, duration: 0.18, ease: 'power2.in' }, 0)
              // Incoming words don't start growing until the outgoing ones
              // have fully finished shrinking — they used to start
              // overlapping the still-shrinking outgoing words for a beat,
              // which is what read as the old font lingering.
              .to(inWords, { scale: 1, duration: 0.32, stagger: 0.025, ease: 'expo.out' }, 0.18)
              .to(inWords, { xPercent: 0, duration: 0.32, stagger: 0.025, ease: 'elastic.out(1, 0.75)' }, 0.18);
          } else {
            // "stack": whole layers travel together through a single
            // clipped window — an odometer-style block flip. The clip
            // window is held FIXED at rowHeight for the whole slide,
            // rather than animated toward the incoming layer's own
            // (possibly shorter/taller) height via syncStackHeight — that
            // used to run concurrently, on its own 0.4s/power2.inOut
            // timing, while the char-slide assumed a constant
            // rowHeight-tall clip; the two fell out of sync mid-transition
            // and both layers ended up visible at once instead of one
            // cleanly masking the other. The height only settles to the
            // incoming layer's real size once the slide (and its clip)
            // is done.
            //
            // Technique ported from Academy Upgrade Hero's own split-flap
            // reveal (initStackReveal there): every letter travels the
            // SAME absolute pixel distance (not a %, so a taller/shorter
            // font stage still stays in sync), with a per-character
            // stagger offset by a further step between the outgoing and
            // incoming letters — that combined diagonal cascade is what
            // reads as a scoreboard flip rather than a flat, uniform wipe.
            // Unlike Hero's one-shot page-load version, the stagger step
            // here is scaled down for long headlines (see staggerStep
            // below) and durations are cut roughly in half, since this
            // replays on every scroll step and has to feel snappy rather
            // than cinematic. It also deliberately does NOT share Hero's
            // single overlapped power4.inOut leg — see the timing block
            // further down: out and in run as two separate, strictly
            // sequential legs here, because unlike Hero (one font, one
            // play) this swaps between two DIFFERENT font families, and
            // any overlap at all reads as the old font not being done.
            var outChars = layerSplits[outIndex].chars;
            var inChars = layerSplits[i].chars;
            var rowHeight = Math.max(layerHeight(outLayer), layerHeight(inLayer));
            // Decorative display fonts (Chillax/Panchang/Sharpie/BN*/...)
            // routinely have ascender/descender ink that overshoots their
            // own CSS line box — offsetHeight only measures the line box,
            // not the real glyph ink extent. Traveling exactly rowHeight
            // (the old behavior) left ZERO margin: outgoing and incoming
            // char boxes were mathematically edge-to-edge for the entire
            // slide, so any font's ink overshoot was exactly what made the
            // two layers visibly touch/overlap mid-flip. travelDistance
            // pads that trip: the clip window itself stays at rowHeight
            // (no layout jump), but both layers now travel PAST it, so
            // there's a real stretch mid-slide where the outgoing layer
            // has already cleared the window and the incoming one hasn't
            // entered it yet — neither text visible at all for a beat,
            // instead of a hairline (or negative) gap between them.
            var travelDistance = rowHeight + Math.max(rowHeight * 0.25, 24);
            var prevOverflow = headlineStack.style.overflow;
            gsap.set(headlineStack, { height: rowHeight, overflow: 'hidden' });
            gsap.set(inChars, { y: travelDistance });
            gsap.set(outChars, { y: 0 });

            activeTween = gsap.timeline({
              onComplete: function () {
                hideLayer(outLayer);
                headlineStack.style.overflow = prevOverflow;
                // Height sync and the pill's reveal now run CONCURRENTLY
                // (same as "pop" mode above), not nested — syncStackHeight
                // waiting for its own 0.3s tween to finish before even
                // STARTING the pill's reveal added a full extra beat of
                // nothing happening on top of the flip's own ~0.7s, which
                // is what made the pill read as gone for far too long
                // before reappearing.
                syncStackHeight(inLayer, true);
                popPillMarks(inLayer, true);
              }
            });

            // Per-char stagger is scaled DOWN as the headline gets longer
            // so the whole flip always lands inside a fixed short budget —
            // a long headline used to spend almost 2s just staggering
            // through its own characters at a flat 0.05s/char before the
            // per-char flight time was even added, which is what read as
            // slow/laggy. duration itself is also cut roughly in half from
            // the one-shot page-load version this was ported from (that
            // one only ever plays once; this replays on every scroll
            // step, so it has to feel snappy, not cinematic).
            var maxChars = Math.max(outChars.length, inChars.length);
            var staggerStep = maxChars > 1 ? Math.min(0.02, 0.18 / maxChars) : 0;
            // Out and in are now two SEPARATE, non-overlapping legs per
            // character, not one cascade with a token stagger between
            // them. The old timing started the incoming char a single
            // staggerStep (~0.02s) after the outgoing one, with both on
            // the same 0.45s duration — so for essentially the entire
            // flip both letters were mid-flight inside the same clip
            // window at once. No amount of extra travel distance fixes
            // that: the old font was still visibly on its way out while
            // the new one was already pushing in behind it, which is
            // exactly the "die alte ist noch nicht fertig" overlap.
            // Each incoming char now only starts once ITS outgoing
            // counterpart has fully left (+ a short GAP where the window
            // is genuinely empty), so the two fonts are never on screen
            // simultaneously.
            //
            // Eases match that split: power3.in accelerates the outgoing
            // letter AWAY (it clears fast instead of lingering — the old
            // power4.inOut started slow, which is what made the old font
            // read as hanging around), power3.out decelerates the
            // incoming one into place. Durations are trimmed so the full
            // two-leg sequence still lands in roughly the same total
            // budget the single overlapped leg used to.
            var outDuration = 0.28;
            var inDuration = 0.34;
            var legGap = 0.07;
            for (var stackI = 0; stackI < maxChars; stackI++) {
              var charStart = stackI * staggerStep;
              if (outChars[stackI]) {
                activeTween.to(outChars[stackI], { y: -travelDistance, duration: outDuration, ease: 'power3.in' }, charStart);
              }
              if (inChars[stackI]) {
                activeTween.to(inChars[stackI], { y: 0, duration: inDuration, ease: 'power3.out' }, charStart + outDuration + legGap);
              }
            }
          }
        };
      }
    }

    /* ── Headline: the pill-mark border only draws once academy-text-
       reveal finishes on the first (initially-active) layer — only
       present at all when transition mode is "crossfade" (see the Liquid
       comment above); for "stack"/"pop" this querySelector simply finds
       nothing and reveal falls through to the immediate-reveal branch,
       identical to how those modes already worked. ── */
    (function setupHeadlinePillSequencing() {
      var firstLayer = headlineLayers[0];
      if (!firstLayer) return;

      function revealMarks(layer) {
        Array.from(layer.querySelectorAll('.academy-pill-mark')).forEach(function (m) {
          m.classList.add('is-revealed');
        });
      }

      var reveal = firstLayer.querySelector('academy-text-reveal');
      if (reveal && !reduceMotion) {
        reveal.addEventListener('academytextreveal:play', function () {
          var lineCount = reveal.querySelectorAll('.academy-text-reveal__line').length || 1;
          var duration = parseFloat(reveal.dataset.duration) || 0.75;
          var stagger = parseFloat(reveal.dataset.stagger) || 0.15;
          var delay = parseFloat(reveal.dataset.delay) || 0;
          var totalMs = (delay + (lineCount - 1) * stagger + duration * 2) * 1000;
          setTimeout(function () { revealMarks(firstLayer); }, totalMs);
        });
      } else if (reveal) {
        // Text-reveal element exists but couldn't set itself up
        // (SplitText/gsap missing) or reduced motion — don't leave the
        // pill permanently undrawn.
        revealMarks(firstLayer);
      }
      // else: no academy-text-reveal at all (stack/pop mode) — the
      // generic scroll-reveal observer in academy-pill-mark.js already
      // handles this layer's marks normally, untouched by any of this.
    })();

    /* ── Headline exit: the headline+text column (not the handle — that
       lives directly in .aps-sticky, not .aps-sticky__inner) fades and
       rises away via yPercent — but only AFTER all 3 stages have fully
       played out. The headline must stay full-size and top-pinned for
       the ENTIRE 3-stage scroll length; this is driven by its own
       separate trigger below (not blended into the 0–1 pin progress),
       starting exactly where the pin trigger's range ends. No scale
       here on purpose — heading and copy stay at their real size the
       whole way out, only position/opacity animate. ── */
    function applyHeadlineExit(exitP) {
      if (!stickyInner || reduceMotion) return;
      var isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) {
        // Mobile: front-load the fade/rise so the column is fully gone
        // (opacity 0, pushed up, hidden, non-interactive) by ~60% of the
        // exit run instead of only at exitP === 1. The mobile exit run is
        // short and the un-pinned .aps-sticky is still a tall box whose
        // content sits in the top third — on devices where the
        // vh/svh/dvh split makes .aps-stage's computed px height come out
        // tight, the testimonial can reach this band while a plain linear
        // fade still has the copy partly visible (the reported text
        // overlap). Finishing early leaves nothing to overlap regardless
        // of the exact geometry.
        var fp = Math.min(exitP / 0.6, 1);
        gsap.set(stickyInner, {
          yPercent: gsap.utils.interpolate(0, -60, fp),
          opacity: gsap.utils.interpolate(1, 0, fp),
          visibility: fp >= 1 ? 'hidden' : 'visible',
          pointerEvents: fp >= 1 ? 'none' : 'auto'
        });
        return;
      }
      gsap.set(stickyInner, {
        yPercent: gsap.utils.interpolate(0, -35, exitP),
        opacity: gsap.utils.interpolate(1, 0, exitP),
        // Clear anything the mobile branch may have latched before a
        // resize/rotation crossed the breakpoint mid-exit.
        visibility: 'visible',
        pointerEvents: 'auto'
      });
    }

    /* ── Pin: GSAP ScrollTrigger, trigger AND pin target are the SAME
       element (.aps-sticky) — the pin:true pattern already proven
       working elsewhere in this theme (Academy Testimonial, Academy Clip
       Mask). `end` is a RELATIVE '+=pxAmount' offset, never a live
       position string like 'bottom bottom' — a position-based end on a
       pinned trigger never actually held in an earlier attempt here.
       The "stage went super narrow" bug was NOT the pin itself: GSAP's
       pin switches the target to position:fixed, and a fixed-position
       element defaults to shrink-wrap its own content width instead of
       keeping the width it had in normal flow. Fixed by adding an
       explicit width:100% to .aps-sticky / .aps-handle-stage in their
       CSS — do not remove the pin over this again, add the width fix
       instead if it recurs.
       .aps-sticky starts in normal flow (see its CSS: position:relative,
       not sticky) — that's what makes the headline scroll in naturally
       before the pin engages; GSAP only switches it to position:fixed
       once 'top' reaches 'top+=header'. pinSpacing:false because
       .aps-stage's own explicit height (--aps-scroll-length) already
       reserves the scroll room; letting GSAP ALSO insert its default
       spacer would double-reserve it and stretch the page longer than
       the "Scroll-Länge" setting intends. The pin distance is computed
       directly from that same setting (data-scroll-length, in vh) minus
       one viewport height, rather than measured live off any element's
       bounding rect. This trigger's progress covers EXACTLY the 3
       equal-length stages and nothing more — the headline exit below
       deliberately starts only once this reaches 1, so the headline
       stays full-size for the whole thing. ── */
    var pinTrigger = null;
    var updateStageHeight = null;
    var syncResponsiveLengths = null;
    // FROZEN small-viewport height. The CSS spacers (.aps-handle-stage /
    // .aps-sticky-spacer) are now sized in svh, and every px scroll-
    // distance calc below has to match that exact reference. Chasing
    // window.visualViewport.height (≈ dvh) meant the pin bounds moved
    // every time the iOS address bar showed/hid mid-scroll → the page
    // jumped. Measure 100svh once via a probe and cache it (re-measured
    // only on a real WIDTH change / orientation, not address-bar height
    // changes — see onViewportChange), so CSS and JS stay locked together
    // and ScrollTrigger.config({ignoreMobileResize:true}) can do its job.
    var _apsVH = 0, _apsVW = 0;
    function measureSvh() {
      var probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;left:0;top:0;width:0;height:100svh;visibility:hidden;pointer-events:none';
      (document.body || document.documentElement).appendChild(probe);
      var h = probe.offsetHeight;
      probe.remove();
      return h || window.innerHeight;
    }
    function viewportHeight() {
      var w = window.innerWidth;
      if (!_apsVH || w !== _apsVW) { _apsVW = w; _apsVH = measureSvh(); }
      return _apsVH;
    }
    if (stage && sticky) {
      function headerOffset() {
        var el = document.querySelector('.header-section');
        return el ? Math.round(el.getBoundingClientRect().height) : 0;
      }
      // Desktop and mobile lengths are authored separately; the mobile
      // pair wins below 768px. scrollLengthVh / revealGapVh are the
      // ACTIVE values, re-picked on every viewport change by
      // syncResponsiveLengths() so a rotation or a desktop<->mobile
      // resize switches cleanly.
      function isMobileViewport() {
        return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
      }
      var scrollLengthDesktopVh = parseFloat(section.dataset.scrollLength) || 320;
      var scrollLengthMobileVh  = parseFloat(section.dataset.scrollLengthMobile) || 240;
      var revealGapDesktopVh    = parseFloat(section.dataset.revealGap) || 100;
      var revealGapMobileVh     = parseFloat(section.dataset.revealGapMobile) || 40;
      var scrollLengthVh = scrollLengthDesktopVh;
      var revealGapVh = revealGapDesktopVh;
      syncResponsiveLengths = function () {
        var m = isMobileViewport();
        scrollLengthVh = m ? scrollLengthMobileVh : scrollLengthDesktopVh;
        revealGapVh    = m ? revealGapMobileVh    : revealGapDesktopVh;
        // .aps-handle-stage / .aps-sticky-spacer size themselves off this
        // CSS var in SCSS — keep it equal to the value the pin maths here
        // uses, or the DOM spacers and the ScrollTrigger ranges drift apart.
        section.style.setProperty('--aps-reveal-gap-vh', revealGapVh);
      };
      syncResponsiveLengths();

      function pinDistance() {
        return Math.max(viewportHeight() * (scrollLengthVh / 100 - 1), 1);
      }
      // How much of a screenful the "Reveal-Vorlauf" setting is worth,
      // e.g. 100 (the default) => 1 full viewport height. Used below
      // instead of a hardcoded "2 viewport heights" so the handle pin's
      // duration always matches whatever that setting is actually set
      // to, not just its default. A function, not a one-time value, for
      // the same live-viewport reason as pinDistance() above.
      function revealGapPx() {
        return viewportHeight() * (revealGapVh / 100);
      }
      // Tail of scroll after the pin releases, over which .aps-sticky__inner
      // fades + rises out. A full viewport on desktop; on mobile the sticky
      // content only occupies the top third and applyHeadlineExit() now
      // front-loads the fade to opacity 0 by ~60% of this run — a full
      // screen here was just dead teal space between the collage and the
      // testimonial ("Abstand extrem hoch" on tall phones). 0.7 (was 0.5):
      // a little extra physical separation so a tight vh/svh/dvh split on
      // some devices can't let the testimonial reach the headline band
      // before this run has ended (text overlap).
      function headlineExitPx() {
        return viewportHeight() * (isMobileViewport() ? 0.7 : 1);
      }

      // .aps-stage's CSS height (--aps-scroll-length, authored in plain
      // vh) is only a no-JS fallback now — once GSAP is running, this
      // OVERRIDES it with the exact px total the triggers below actually
      // consume: 2 reveal-gaps (handle-stage + spacer) + the sticky
      // pin's own duration + a FULL extra viewport for the headline-exit
      // — .aps-sticky un-pins (back to a normal, non-fixed 100vh box) the
      // instant pinTrigger ends, and a normal 100vh box needs a full
      // 100vh of further scrolling to actually clear the viewport; a
      // smaller buffer here left it still half on-screen, in normal
      // document flow, by the time .aps-stage's box ended and the
      // testimonial's own entrance trigger started firing — i.e. the
      // exact "testimonial scrolls in before the heading is actually
      // gone" overlap. Computing it here, in the SAME viewportHeight()-
      // based px space as every trigger below, is what keeps it from
      // drifting out of sync with them — a CSS vh/dvh calc can't
      // reliably match px math built on window.visualViewport, since
      // mobile browsers don't agree on what plain "vh" itself refers to
      // (large vs. small viewport), so a fixed vh-based buffer that
      // happened to match once silently stops matching the moment
      // either side's reference viewport changes.
      updateStageHeight = function () {
        stage.style.height = (2 * revealGapPx() + pinDistance() + headlineExitPx()) + 'px';
      };
      updateStageHeight();

      /* Handle pin: same trigger===target pin:true recipe, engaging
         EARLIER than .aps-sticky's own pin below — .aps-handle-stage
         sits right before .aps-sticky-spacer in the document, so its
         natural (pre-pin) top reaches the pin line sooner, by exactly
         TWO reveal-gaps (its own height + the spacer's, both driven by
         the same "Reveal-Vorlauf" setting), than .aps-sticky's does.
         Its pin duration bridges that gap plus .aps-sticky's own full
         pin duration, so both release at the same scroll position — the
         handle stays pinned for the headline's entire pinned lifetime,
         not just the lead-in. */
      if (handleStage) {
        triggers.push(ScrollTrigger.create({
          trigger: handleStage,
          pin: true,
          pinSpacing: false,
          start: function () {
            var h = headerOffset();
            return h ? 'top top+=' + h : 'top top';
          },
          end: function () {
            return '+=' + (2 * revealGapPx() + pinDistance());
          },
          scrub: true
        }));
      }

      pinTrigger = ScrollTrigger.create({
        trigger: sticky,
        pin: true,
        pinSpacing: false,
        start: function () {
          var h = headerOffset();
          return h ? 'top top+=' + h : 'top top';
        },
        end: function () {
          return '+=' + pinDistance();
        },
        scrub: true,
        // The collage appears as soon as the headline is pinned. Its
        // individual entrance triggers provide the only visual motion:
        // every card rises in from below. Do not fade this layer in here;
        // that made the cards look as if they were materialising in place.
        onEnter: function () {
          if (imagesLayer && !reduceMotion) imagesLayer.style.visibility = 'visible';
        },
        onEnterBack: function () {
          if (imagesLayer && !reduceMotion) imagesLayer.style.visibility = 'visible';
        },
        onLeaveBack: function () {
          if (imagesLayer && !reduceMotion) imagesLayer.style.visibility = 'hidden';
        },
        // ScrollTrigger does not guarantee onEnter when the browser restores
        // an anchor/history position already inside a pin. Derive the layer
        // visibility from the refreshed range as well, without moving the
        // page or replaying the entrance fade.
        onRefresh: function (self) {
          if (imagesLayer && !reduceMotion) {
            imagesLayer.style.visibility = self.scroll() >= self.start ? 'visible' : 'hidden';
          }
          var p = self.progress;
          var i = p < 1 / 3 ? 0 : (p < 2 / 3 ? 1 : 2);
          applyStage(i);
          stageColorProgress = p * 2;
          applyGradientColors();
        },
        onUpdate: function (self) {
          var p = self.progress;
          var i = p < 1 / 3 ? 0 : (p < 2 / 3 ? 1 : 2);
          applyStage(i);
          stageColorProgress = p * 2;
          applyGradientColors();
        }
      });
      triggers.push(pinTrigger);

      // Headline exit: starts exactly where the pin trigger's range
      // ends (the moment .aps-sticky would naturally unstick) and runs
      // over a FULL further viewport height, matching .aps-sticky's own
      // 100vh — that's exactly how much normal (unpinned) scrolling it
      // takes for that box to actually leave the screen, so the
      // testimonial (which starts once .aps-stage's box ends, sized to
      // match this) never begins appearing while the heading/copy are
      // still physically on screen. Entirely separate from the 0–1
      // progress above, so nothing here can fire early during stage 3.
      if (stickyInner && !reduceMotion) {
        triggers.push(ScrollTrigger.create({
          trigger: stage,
          start: function () { return pinTrigger.end; },
          end: function () { return pinTrigger.end + headlineExitPx(); },
          scrub: true,
          onUpdate: function (self) { applyHeadlineExit(self.progress); }
        }));
      }
    } else if (imagesLayer) {
      imagesLayer.style.visibility = 'visible';
      imagesLayer.style.opacity = '1';
    }

    /* ── Phase 4: testimonial's own entrance reveal, same recipe as the
       top one (flat dome → flattening rectangle via scaleY + corner
       radius) but rounded on the BOTTOM corners and scoped locally to
       .aps-testimonial itself — it isn't pinned/sticky like the stage
       is, so this needs no .aps-bg-style sticky shell, just a plain
       absolutely-positioned shape that grows in as the testimonial
       scrolls into view. Drives the tail end of the gradient color
       crossfade (phase 3→4) at the same time, via the SAME
       --aps-gradient-* custom properties (inherited down from .aps-
       section), so its own color picks up exactly where the frozen top
       reveal's color left off.
       transformOrigin is TOP (50% 0%), not bottom: it needs to grow
       DOWNWARD from where it meets the already-colored area above, not
       outward from its own bottom edge. scaleY only ever reaches 1 (not
       past it, unlike the top reveal's deliberate 1.08/2.2 overshoot) —
       this shape's own box already exactly matches .aps-testimonial's
       height, so scaleY:1 is "fully grown, flush with the box".
       Also pulled up by a matching yPercent, up to -100% at full growth
       — same mechanic as the top reveal's own upward yPercent (kept at
       -25% there), just applied here at the opposite end: without it
       the shape's growing height pushes its own bottom edge past
       .aps-testimonial's box, overlapping into whatever section
       follows this one. */
    var testimonialReveal = testimonial ? testimonial.querySelector('[data-aps-testimonial-reveal]') : null;
    if (testimonialReveal) gsap.set(testimonialReveal, { transformOrigin: '50% 0%' });

    function applyTestimonialReveal(progress) {
      if (!testimonialReveal) return;
      var p = Math.min(Math.max(progress, 0), 1);
      testimonialReveal.style.visibility = p > 0 ? 'visible' : 'hidden';
      var radius = gsap.utils.interpolate(100, 0, p) + '%';
      gsap.set(testimonialReveal, {
        scaleY: gsap.utils.interpolate(0.15, 1, p),
        yPercent: -gsap.utils.interpolate(0, 100, p),
        borderBottomLeftRadius: radius,
        borderBottomRightRadius: radius
      });
      // Once fully grown the gradient has long faded to the plain edge
      // color — blurring a flat fill is invisible but still costs a
      // compositor layer for the rest of the page's scroll (measurable on
      // weaker phones). Dropped once it stops contributing; re-armed on
      // scroll-back before that point.
      testimonialReveal.classList.toggle('is-settled', p >= 0.98);
    }

    applyTestimonialReveal(0);

    if (testimonial) {
      triggers.push(ScrollTrigger.create({
        trigger: testimonial,
        start: 'top bottom',
        end: 'top 55%',
        scrub: true,
        onUpdate: function (self) {
          testimonialColorProgress = self.progress;
          applyGradientColors();
          applyTestimonialReveal(self.progress);
        }
      }));
    }

    /* Shared pointer-velocity tracker for every card's inertia fling (see
       below) — one global listener instead of one per card, and properly
       removed on section unload (see cleanupFns) instead of
       accumulating a fresh copy on every theme-editor re-render. Same
       mechanic as Academy USP List's stage pointer effect. */
    if (typeof InertiaPlugin !== 'undefined') gsap.registerPlugin(InertiaPlugin);
    // Desktop-pointer only: on touch a scroll IS a pointermove, so the
    // shared window pointermove tracker below added per-frame velocity math
    // to the scroll path and the per-card pointerenter fling fired
    // underneath the scroll gesture. Gates both the tracker and the flings.
    var hasInertia = typeof InertiaPlugin !== 'undefined'
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var pointerLastX = 0, pointerLastY = 0, pointerDeltaX = 0, pointerDeltaY = 0, pointerRaf = null;
    if (hasInertia && !reduceMotion) {
      var onPointerMove = function (event) {
        if (pointerRaf) return;
        pointerRaf = requestAnimationFrame(function () {
          pointerDeltaX = event.clientX - pointerLastX;
          pointerDeltaY = event.clientY - pointerLastY;
          pointerLastX = event.clientX;
          pointerLastY = event.clientY;
          pointerRaf = null;
        });
      };
      window.addEventListener('pointermove', onPointerMove);
      cleanupFns.push(function () {
        window.removeEventListener('pointermove', onPointerMove);
        if (pointerRaf) cancelAnimationFrame(pointerRaf);
      });
    }
    var clampInertiaVelocity = gsap.utils.clamp(-900, 900);

    /* Captions use their card's entrance trigger instead of a second
       ScrollTrigger on an absolutely positioned element. Absolute children
       of a pinned subtree do not have a stable document position, which
       meant their independent triggers could miss their enter point.
       Always Academy Text Reveal (same component the handle uses) — no
       separate per-section reveal-style setting; that dropdown used to
       offer fade/stack/none alternatives that just added ways for a
       caption to end up wired to the wrong (or no) reveal path. */
    var captionPlayers = new Map();
    if (!reduceMotion) {
      images.forEach(function (host) {
        var cap = host.querySelector('[data-aps-caption]');
        if (!cap || !cap.querySelector('academy-text-reveal')) return;

        var playCaption = function () {
          var tries = 0;
          (function attempt() {
            // Pin-spacer swaps can briefly reconnect custom elements.
            // Resolve the node on every frame, rather than holding a
            // stale pre-pin reference, and keep waiting long enough for
            // the globally deferred text-reveal asset to upgrade it.
            var tr = cap.querySelector('academy-text-reveal');
            if (tr && typeof tr.playReveal === 'function') { tr.playReveal(); return; }
            if (tries++ < 180) { requestAnimationFrame(attempt); return; }

            // Do not leave a caption permanently invisible if GSAP's
            // custom element failed to upgrade. The normal path above is
            // still Academy Text Reveal; this is only a visible fallback.
            gsap.set(cap.querySelectorAll('.academy-text-reveal__line'), { opacity: 1 });
            gsap.set(cap.querySelectorAll('.academy-text-reveal__block'), { scaleX: 0 });
            if (tr) tr.style.opacity = '1';
          })();
        };

        var played = false;
        captionPlayers.set(host, function () {
          if (played) return;
          played = true;
          playCaption();
        });
      });
    }

    /* ── Image containers: entrance grow+rotate+rise, drift,
       pointer-inertia fling ── */
    images.forEach(function (outer, imgIndex) {
      // The entrance + fling run on .aps-image__group (the card AND its
      // caption together) so the caption tracks the tile rigidly instead
      // of hanging at its resting spot while the card scrolls/scales in.
      // `card` is that group element throughout this loop; fall back to
      // the bare card element for an older cached section with no wrapper.
      var card = outer.querySelector('[data-aps-group]') || outer.querySelector('[data-aps-card]');
      // Counter-scale target for the caption: the group scale-in below
      // would otherwise shrink the caption TEXT along with the tile. Scale
      // the caption's inner node by 1/s so it stays a constant 14–16px.
      var capOuter = outer.querySelector('[data-aps-caption]');
      var capScaleEl = capOuter
        ? (capOuter.querySelector('[data-aps-caption-inner]') || capOuter.querySelector('academy-text-reveal') || capOuter)
        : null;
      // `data-speed` is authored per image in the section editor. Keep the
      // value on this card all the way through to the drift distance; the
      // former fixed 34px range made 0.1 and 3 feel almost identical.
      var speed = Math.max(0.1, parseFloat(outer.dataset.speed) || 1);
      var playCaption = captionPlayers.get(outer);
      var baseRotationDeg = card ? parseFloat(getComputedStyle(card).getPropertyValue('--aps-base-rotation')) || 0 : 0;

      if (card) {
        // The whole card (frame + media together) grows in as ONE unit —
        // no separate media-mask "unsqueeze". The frame is never smaller
        // than the card and the image always fills the frame; only the
        // card's own scale/rotation/rise animate.
        // Rise distance is a PLAIN PX value based on viewportHeight(), not
        // yPercent (a % of the CARD's own height) — yPercent only ever
        // guaranteed the card started somewhere below its resting spot,
        // not actually below the viewport's bottom edge: a card resting
        // near the top of the screen only needed to travel a small
        // fraction of the viewport to clear it, so 130% of its OWN
        // (often much shorter) height still left it starting from
        // somewhere already inside the visible area — reading as "grows
        // in place" rather than "scrolls in from outside". A flat
        // viewportHeight()*1.1 px offset guarantees the start position is
        // below the bottom edge regardless of where the card rests.
        function riseDistance() {
          return viewportHeight() * 1.1;
        }
        gsap.set(card, {
          transformOrigin: '50% 50%',
          rotation: baseRotationDeg + (reduceMotion ? 0 : 18),
          scale: reduceMotion ? 1 : 0.55,
          y: reduceMotion ? 0 : riseDistance()
        });
        if (capScaleEl && !reduceMotion) gsap.set(capScaleEl, { scale: 1 / 0.55 });

        // Entrance window is anchored to the pin (never earlier) and
        // spaced out across the pin's FULL scroll distance by DOM order
        // (imgIndex / total), NOT bunched into a tiny ~1.5-viewport
        // window right at the start of a pin that can run 200+
        // viewports long (the old flat 0.12-viewport-per-card stagger
        // did exactly that). A prior attempt derived the spacing from
        // each card's own --aps-y via getComputedStyle(outer).top —
        // reverted: relying on live layout measurement here proved
        // fragile (only the last card ended up animating), so this
        // sticks to a plain index fraction, no DOM measurement.
        function entranceStart() {
          if (!pinTrigger) {
            return outer.getBoundingClientRect().top + window.pageYOffset - viewportHeight() * 0.9;
          }
          var pinSpan = pinTrigger.end - pinTrigger.start;
          var totalSpan = Math.max(1, images.length - 1);
          var indexFraction = imgIndex / totalSpan;
          // Leave enough room at the tail for the card to fully finish its
          // own rise (entranceRise()) before the pin itself ends.
          var maxStart = pinTrigger.end - entranceRise();
          return gsap.utils.clamp(
            pinTrigger.start,
            Math.max(pinTrigger.start, maxStart),
            pinTrigger.start + indexFraction * pinSpan * 0.85
          );
        }
        function entranceRise() {
          return viewportHeight() * 0.9;
        }
        triggers.push(ScrollTrigger.create({
          trigger: outer,
          start: entranceStart,
          end: function () { return entranceStart() + entranceRise(); },
          scrub: apsImgScrub,
          onUpdate: function (self) {
            if (reduceMotion) return;
            var p = self.progress;
            var s = gsap.utils.interpolate(0.55, 1, p);
            gsap.set(card, {
              scale: s,
              rotation: baseRotationDeg + (1 - p) * 18,
              y: gsap.utils.interpolate(riseDistance(), 0, p)
            });
            if (capScaleEl) gsap.set(capScaleEl, { scale: 1 / s });
          }
        }));

        // Caption text-reveal: its OWN trigger, independent of the card's
        // synthetic entrance window above — fires once the tile's actual
        // authored position (--aps-y, i.e. its real place inside .aps-stage,
        // which scrolls normally; only .aps-sticky is pinned) reaches the
        // viewport, not once the card's index-based entrance progress hits
        // some threshold. A plain string start/end lets ScrollTrigger track
        // `outer`'s real, un-transformed layout position itself (refresh-
        // safe), rather than re-deriving it by hand.
        if (playCaption) {
          triggers.push(ScrollTrigger.create({
            trigger: outer,
            start: 'top bottom',
            onEnter: playCaption,
            onEnterBack: playCaption,
            onRefresh: function (self) {
              if (self.progress > 0) playCaption();
            }
          }));
        }
      }

      if (!reduceMotion) {
        /* Container-level differential parallax. A card at speed:1 tracks
           normal scroll exactly (no extra motion, its z-index/position
           stays put relative to the page). Cards above/below 1 drift by
           (1 - speed) * travel over the WHOLE stage's scroll range,
           monotonically (no easing back to 0 mid-way) — that's what makes
           a speed:1.3 card visibly race ahead of a speed:0.8 one and pass
           behind/in front of it (via z-index) as the page scrolls, rather
           than the two just swaying in place around their rest position.
           Same mechanic on desktop and mobile; only `travel` (how many
           viewport-heights of total relative drift the extremes get) and
           the scrub smoothing differ. TUNE LIVE — single knob per
           breakpoint. */
        triggers.push(ScrollTrigger.create({
          trigger: stage || outer,
          start: 'top bottom',
          end: 'bottom top',
          scrub: apsIsMobile ? true : 1.2,
          onUpdate: function (self) {
            var travel = viewportHeight() * (apsIsMobile ? 1.3 : 2);
            var offset = (1 - speed) * travel * (self.progress - 0.5);
            gsap.set(outer, { y: offset });
          }
        }));

        /* Pointer-inertia fling — same mechanic as Academy USP List's
           stage pointer effect: on pointerenter, throw the element via
           InertiaPlugin using the tracked approach velocity, settling
           back to rest on its own.
           USP List flings ONE element per pointerenter. This used to fling
           BOTH `card` (x + yPercent) AND `outer` (x + rotation) on the same
           event — two overlapping tweens both writing `x` compound into a
           much larger total displacement than either alone, which is what
           read as "much more sensitive" than USP List's single-tween feel.
           `card` now only takes the vertical lift (yPercent); `outer` alone
           owns x + the entry-angle spin, so there's exactly one tween per
           axis, matching USP List's calibration. */
        if (hasInertia && card) {
          outer.addEventListener('pointerenter', function (event) {
            // yPercent, NOT y: the entrance trigger above drives the card's
            // `y` (its rise-in) on every scrubbed scroll tick, and with
            // scrub that keeps firing for ~1s after any scroll — so a fling
            // written to the same `y` gets stomped mid-flight. yPercent
            // composes with y in the transform, so the lift still reads
            // identically while staying on a property nothing else touches.
            gsap.to(card, {
              inertia: {
                // yPercent velocity is %/s, not px/s — its own clamp.
                yPercent: { velocity: gsap.utils.clamp(-160, 160, 3 * pointerDeltaY), end: 0 },
                resistance: 240
              }
            });

            // Additional fling on the CONTAINER itself, same event —
            // this is the fuller USP List mechanic (x + an entry-angle
            // spin, from the cross product of approach offset/velocity),
            // not just x/y like the card gets above. `outer` has no
            // competing rotation tween (unlike card, whose rotation is
            // scroll-driven), so it can safely take the spin; `y` is
            // left out here on purpose — the container drift trigger
            // above sets `outer`'s y on every scroll tick, and letting
            // inertia fight over the same property is exactly what card
            // avoids with rotation.
            var rect = outer.getBoundingClientRect();
            var offsetX = event.clientX - (rect.left + rect.width / 2);
            var offsetY = event.clientY - (rect.top + rect.height / 2);
            var spin = (offsetX * pointerDeltaY - offsetY * pointerDeltaX) / (Math.hypot(offsetX, offsetY) || 1);
            gsap.to(outer, {
              inertia: {
                x: { velocity: clampInertiaVelocity(8 * pointerDeltaX), end: 0 },
                rotation: { velocity: gsap.utils.clamp(-40, 40)(8 * spin), end: 0 },
                resistance: 280
              }
            });
          });
        }
      }
    });

    // The layer starts hidden only to prevent pre-JS FOUC. At this point
    // every card has already been placed below the viewport by its entrance
    // transform, so making the layer visible cannot pop an image into view;
    // it lets each card physically scroll in from below instead.
    if (imagesLayer && !reduceMotion) imagesLayer.style.visibility = 'visible';

    // Custom @font-face fonts (font-display: swap) can still be showing
    // fallback-font metrics at the point every trigger above computed its
    // start/end — once the real font swaps in and reflows the (often
    // very large) headline, those measurements go stale. A single refresh
    // once fonts are actually ready re-measures everything.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    }

    // Only react to a REAL viewport change — a width change (rotation,
    // desktop resize). Height-only "resize" events are the iOS address bar
    // showing/hiding; viewportHeight() is frozen against those on purpose
    // (see above) and ScrollTrigger.config({ignoreMobileResize:true})
    // already tells ScrollTrigger to ignore them. There is deliberately NO
    // visualViewport 'resize' listener any more — it fired on every
    // address-bar nudge and its ScrollTrigger.refresh() re-measured every
    // pinned section on the page mid-scroll, which is the "URL bar moves →
    // all pinning/scrub sections jump" bug.
    var _apsLastW = window.innerWidth;
    var onViewportChange = function () {
      if (window.innerWidth === _apsLastW) return;
      _apsLastW = window.innerWidth;
      if (syncResponsiveLengths) syncResponsiveLengths();
      if (updateStageHeight) updateStageHeight();
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', onViewportChange);
    cleanupFns.push(function () { window.removeEventListener('resize', onViewportChange); });

    /* No viewport "disable the triggers while off-screen" gate: a scrubbed
       ScrollTrigger outside its own start/end range already fires no
       onUpdate on further scroll, so the saving was near-zero — while
       disable(false) + enable()+refresh() on approach, on a fast mobile
       scroll where the IntersectionObserver callback lands late, meant the
       section could be reached with its pin still disabled → scrolled in
       un-pinned, then snapped back on enable ("zurückgeleitet"). */

    section.__apsCleanup = function () {
      triggers.forEach(function (t) { t.kill(); });
      triggers = [];
      cleanupFns.forEach(function (fn) { fn(); });
      cleanupFns = [];
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { withGSAP(initSection); });
  } else {
    withGSAP(initSection);
  }

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.detail && event.detail.sectionId === SECTION_ID) {
      var el = document.getElementById('aps-' + SECTION_ID);
      if (el && el.__apsCleanup) el.__apsCleanup();
    }
  });
})();
